import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Pencil, Trash2, DollarSign, Eye } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function SupplierMaster() {
  const [suppliers, setSuppliers] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [isLedgerDialogOpen, setIsLedgerDialogOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [supplierLedger, setSupplierLedger] = useState(null);
  const [payments, setPayments] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    contact_person: '',
    email: '',
    phone: '',
    address: ''
  });
  const [paymentData, setPaymentData] = useState({
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: '',
    reference: ''
  });

  useEffect(() => {
    fetchSuppliers();
    fetchLedgers();
  }, []);

  const fetchSuppliers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/suppliers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuppliers(response.data);
    } catch (error) {
      toast.error('Failed to fetch suppliers');
    }
  };

  const fetchLedgers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/suppliers/ledger/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLedgers(response.data);
    } catch (error) {
      console.error('Failed to fetch ledgers');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');

      if (editingSupplier) {
        await axios.put(`${BACKEND_URL}/api/suppliers/${editingSupplier.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Supplier updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/suppliers`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Supplier created successfully');
      }

      setIsDialogOpen(false);
      setEditingSupplier(null);
      setFormData({ name: '', contact_person: '', email: '', phone: '', address: '' });
      fetchSuppliers();
      fetchLedgers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (supplier) => {
    setEditingSupplier(supplier);
    setFormData({
      name: supplier.name,
      contact_person: supplier.contact_person,
      email: supplier.email,
      phone: supplier.phone,
      address: supplier.address
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this supplier?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/suppliers/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Supplier deleted successfully');
      fetchSuppliers();
      fetchLedgers();
    } catch (error) {
      toast.error('Failed to delete supplier');
    }
  };

  const openNewDialog = () => {
    setEditingSupplier(null);
    setFormData({ name: '', contact_person: '', email: '', phone: '', address: '' });
    setIsDialogOpen(true);
  };

  const openPaymentDialog = (supplier) => {
    setSelectedSupplier(supplier);
    setPaymentData({
      amount: '',
      payment_date: new Date().toISOString().split('T')[0],
      payment_method: '',
      reference: ''
    });
    setIsPaymentDialogOpen(true);
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const payload = {
        supplier_id: selectedSupplier.id,
        amount: parseFloat(paymentData.amount),
        payment_date: new Date(paymentData.payment_date).toISOString(),
        payment_method: paymentData.payment_method,
        reference: paymentData.reference
      };

      await axios.post(`${BACKEND_URL}/api/payments`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Payment recorded successfully');
      setIsPaymentDialogOpen(false);
      fetchLedgers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    }
  };

  const viewLedger = async (supplier) => {
    try {
      const token = localStorage.getItem('token');
      const [ledgerRes, paymentsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/suppliers/${supplier.id}/ledger`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/payments?supplier_id=${supplier.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setSupplierLedger(ledgerRes.data);
      setPayments(paymentsRes.data);
      setIsLedgerDialogOpen(true);
    } catch (error) {
      toast.error('Failed to fetch ledger details');
    }
  };

  const getLedgerForSupplier = (supplierId) => {
    return ledgers.find(l => l.supplier_id === supplierId) || { total_due: 0, total_paid: 0, balance: 0 };
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-extrabold text-slate-900 tracking-tight font-heading" data-testid="supplier-master-title">Supplier Master</h1>
          <p className="text-slate-600 mt-2">Manage your suppliers and payment ledgers</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNewDialog} className="bg-slate-900 hover:bg-slate-800" data-testid="add-supplier-button">
              <Plus className="w-4 h-4 mr-2" />
              Add Supplier
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md" aria-describedby="supplier-form-description">
            <DialogHeader>
              <DialogTitle>{editingSupplier ? 'Edit Supplier' : 'Add New Supplier'}</DialogTitle>
            </DialogHeader>
            <p id="supplier-form-description" className="sr-only">
              {editingSupplier ? 'Edit the details of an existing supplier' : 'Add a new supplier to your database'}
            </p>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="supplier-form">
              <div className="space-y-2">
                <Label htmlFor="name">Supplier Name</Label>
                <Input
                  id="name"
                  data-testid="supplier-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact_person">Contact Person</Label>
                <Input
                  id="contact_person"
                  data-testid="supplier-contact-input"
                  value={formData.contact_person}
                  onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  data-testid="supplier-email-input"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  data-testid="supplier-phone-input"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  data-testid="supplier-address-input"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  required
                />
              </div>
              <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" data-testid="submit-supplier-button">
                {editingSupplier ? 'Update Supplier' : 'Create Supplier'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs defaultValue="suppliers" className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="suppliers" data-testid="suppliers-tab">Suppliers</TabsTrigger>
          <TabsTrigger value="ledgers" data-testid="ledgers-tab">Payment Ledgers</TabsTrigger>
        </TabsList>

        <TabsContent value="suppliers">
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="p-0">
              <div className="rounded-xl border border-slate-200 overflow-hidden">
                <table className="w-full" data-testid="suppliers-table">
                  <thead className="bg-slate-50">
                    <tr className="border-b border-slate-200">
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Contact</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Phone</th>
                      <th className="px-6 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {suppliers.map((supplier) => (
                      <tr key={supplier.id} className="hover:bg-slate-50/50 transition-colors border-b border-slate-100 last:border-0" data-testid={`supplier-row-${supplier.id}`}>
                        <td className="px-6 py-4 text-sm font-medium text-slate-900">{supplier.name}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{supplier.contact_person}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{supplier.email}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{supplier.phone}</td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => viewLedger(supplier)}
                              data-testid={`view-ledger-${supplier.id}`}
                              className="text-sky-600 hover:text-sky-700 hover:bg-sky-50"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openPaymentDialog(supplier)}
                              data-testid={`add-payment-${supplier.id}`}
                              className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                            >
                              <DollarSign className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleEdit(supplier)}
                              data-testid={`edit-supplier-${supplier.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDelete(supplier.id)}
                              data-testid={`delete-supplier-${supplier.id}`}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {suppliers.length === 0 && (
                  <div className="p-8 text-center text-slate-600">
                    No suppliers found. Add your first supplier to get started.
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="ledgers">
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="p-0">
              <div className="rounded-xl border border-slate-200 overflow-hidden">
                <table className="w-full" data-testid="ledgers-table">
                  <thead className="bg-slate-50">
                    <tr className="border-b border-slate-200">
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Supplier</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Total Due</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Total Paid</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledgers.map((ledger) => (
                      <tr key={ledger.supplier_id} className="hover:bg-slate-50/50 transition-colors border-b border-slate-100 last:border-0" data-testid={`ledger-row-${ledger.supplier_id}`}>
                        <td className="px-6 py-4 text-sm font-medium text-slate-900">{ledger.supplier_name}</td>
                        <td className="px-6 py-4 text-sm text-red-700 font-semibold">₹{ledger.total_due.toFixed(2)}</td>
                        <td className="px-6 py-4 text-sm text-emerald-700 font-semibold">₹{ledger.total_paid.toFixed(2)}</td>
                        <td className="px-6 py-4 text-sm font-bold">
                          <Badge className={ledger.balance > 0 ? 'bg-red-50 text-red-700 border-red-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}>
                            ₹{ledger.balance.toFixed(2)}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {ledgers.length === 0 && (
                  <div className="p-8 text-center text-slate-600">
                    No ledger data available
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Payment Dialog */}
      <Dialog open={isPaymentDialogOpen} onOpenChange={setIsPaymentDialogOpen}>
        <DialogContent className="max-w-md" aria-describedby="payment-form-description">
          <DialogHeader>
            <DialogTitle>Record Payment - {selectedSupplier?.name}</DialogTitle>
          </DialogHeader>
          <p id="payment-form-description" className="sr-only">Record a payment for the selected supplier</p>
          <form onSubmit={handlePaymentSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="amount">Amount</Label>
              <Input
                id="amount"
                data-testid="payment-amount-input"
                type="number"
                step="0.01"
                value={paymentData.amount}
                onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="payment_date">Payment Date</Label>
              <Input
                id="payment_date"
                data-testid="payment-date-input"
                type="date"
                value={paymentData.payment_date}
                onChange={(e) => setPaymentData({ ...paymentData, payment_date: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="payment_method">Payment Method</Label>
              <Input
                id="payment_method"
                data-testid="payment-method-input"
                value={paymentData.payment_method}
                onChange={(e) => setPaymentData({ ...paymentData, payment_method: e.target.value })}
                placeholder="e.g., Cash, Check, Bank Transfer"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reference">Reference</Label>
              <Input
                id="reference"
                data-testid="payment-reference-input"
                value={paymentData.reference}
                onChange={(e) => setPaymentData({ ...paymentData, reference: e.target.value })}
                placeholder="e.g., Check Number, Transaction ID"
                required
              />
            </div>
            <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" data-testid="submit-payment-button">
              Record Payment
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Ledger Details Dialog */}
      <Dialog open={isLedgerDialogOpen} onOpenChange={setIsLedgerDialogOpen}>
        <DialogContent className="max-w-3xl" aria-describedby="ledger-details-description">
          <DialogHeader>
            <DialogTitle>Ledger Details - {supplierLedger?.supplier_name}</DialogTitle>
          </DialogHeader>
          <p id="ledger-details-description" className="sr-only">View detailed ledger information for the selected supplier</p>
          {supplierLedger && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-slate-600">Total Due</p>
                    <p className="text-2xl font-bold text-red-700">₹{supplierLedger.total_due.toFixed(2)}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-slate-600">Total Paid</p>
                    <p className="text-2xl font-bold text-emerald-700">₹{supplierLedger.total_paid.toFixed(2)}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-slate-600">Balance</p>
                    <p className={`text-2xl font-bold ${supplierLedger.balance > 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                      ₹{supplierLedger.balance.toFixed(2)}
                    </p>
                  </CardContent>
                </Card>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-3">Payment History</h3>
                <div className="rounded-lg border border-slate-200 overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr className="border-b border-slate-200">
                        <th className="px-4 py-2 text-left text-xs font-semibold text-slate-700">Date</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-slate-700">Amount</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-slate-700">Method</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-slate-700">Reference</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map((payment) => (
                        <tr key={payment.id} className="border-b border-slate-100 last:border-0">
                          <td className="px-4 py-2 text-sm text-slate-600">{new Date(payment.payment_date).toLocaleDateString()}</td>
                          <td className="px-4 py-2 text-sm font-semibold text-emerald-700">₹{payment.amount.toFixed(2)}</td>
                          <td className="px-4 py-2 text-sm text-slate-600">{payment.payment_method}</td>
                          <td className="px-4 py-2 text-sm text-slate-600">{payment.reference}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {payments.length === 0 && (
                    <div className="p-4 text-center text-slate-600 text-sm">
                      No payments recorded yet
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default SupplierMaster;